import { analyticsProviders } from '../lib/analytics.js';
import Footer from '../components/Footer.jsx';
import Header from '../components/Header.jsx';
import { Eyebrow } from '../components/Section.jsx';
import Seo from '../components/Seo.jsx';
import { CONTACT_EMAIL } from '../lib/site-config.js';

// Written against what the site actually does. If a data flow changes
// (analytics added, a new processor), this page changes in the same PR.

function PolicySection({ kicker, title, children }) {
  return (
    <section className="border-t border-navy-100 py-6">
      <Eyebrow text={kicker} />
      <h2 className="mb-3 font-display text-xl font-semibold text-navy-900">{title}</h2>
      {/* prose-navy, not a size utility: the same 17px serif as the guides, and
          at the read measure about 72 to 78 characters a line. */}
      <div className="prose-navy space-y-3 text-navy-800">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="mx-auto max-w-brief px-4 pt-6 sm:px-6">
      <Seo
        title="Privacy · Charleston.Vacations"
        description="What Charleston.Vacations collects, why, and how to remove it. Plain language, no surprises."
      />
      <Header />
      {/* The article runs at the narrower long-form measure while the masthead
          stays at `brief` width, so the header keeps matching every other page. */}
      <article className="mx-auto max-w-read">
        <div className="mt-8 border-b border-navy-100 pb-6">
          <h1 className="font-display text-3xl font-semibold text-navy-900 sm:text-4xl">Privacy</h1>
          <p className="mt-2 text-navy-600">
            Plain language, no surprises. Effective September 19, 2026.
          </p>
        </div>

        <PolicySection kicker="The short version" title="What we collect and why">
          <p>
            Two things given to us by you: an email address if you subscribe to the
            morning brief, and your booking details if you book the house. Beyond that we
            count how the site is used, without cookies and without anything that identifies
            you: which pages get read and how far the booking steps get. We don't run ads and
            we don't sell or share data with marketers.
          </p>
        </PolicySection>

        <PolicySection kicker="The daily email" title="Subscriptions">
          <p>
            Subscribing sends one email asking you to confirm the address. Nothing goes on
            the list until you click the link in it, so nobody can sign you up by typing
            your address into our form. If you ignore that email we do not write again.
          </p>
          <p>
            Once confirmed, we store your email address with the date, used for one thing:
            sending you the brief each morning. Every email includes a one-click
            unsubscribe link that removes your address immediately. You can also write to{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-navy-700 underline">{CONTACT_EMAIL}</a>{' '}
            and we'll remove it by hand. Emails are delivered through Resend, our email
            provider, which processes your address solely to deliver the message.
          </p>
          <p>
            To stop one person signing up hundreds of addresses, the sign-up form counts
            requests per connection for an hour at a time. That counter holds a number and
            a one-way hash of the connection's address, never the address itself, and it is
            never used for anything but the count.
          </p>
        </PolicySection>

        <PolicySection kicker="Booking the house" title="Reservations">
          <p>
            Booking direct collects your name, email, phone, party size, and dates, used to
            create and manage your reservation. Payment happens on our booking system's secure
            payment page; your card number never touches our servers. The reservation is created
            in OwnerRez, the property-management system that also syncs the calendar with
            Airbnb and Vrbo. We keep a copy of the booking record (no payment card data) to
            show your confirmation and support your stay.
          </p>
        </PolicySection>

        <PolicySection kicker="On your device" title="Cookies & local storage">
          <p>
            No advertising cookies. Usage counting is first-party and cookieless: when you
            open a page or take a booking step (pick dates, get a quote, click Book direct),
            the page sends us the name of that page or step and, for a quote, the number of
            nights. We store no IP address, no browser details, and no identifier of any kind,
            so the counts cannot be tied back to you.
            {analyticsProviders.ga && (
              <>
                {' '}We also use Google Analytics 4 with IP anonymization, which sets its own
                cookies; Google's privacy policy covers how it handles that data.
              </>
            )}{' '}
            The site remembers one preference in your browser's local storage: which persona
            you last selected for the brief. It stays on your device and is never sent to us.
          </p>
        </PolicySection>

        <PolicySection kicker="Plumbing" title="Hosting & logs">
          <p>
            The site runs on Netlify, which keeps standard server logs (IP address, request
            path, timestamp) for security and operations. We don't combine those logs with
            anything else.
          </p>
        </PolicySection>

        <PolicySection kicker="Your data, your call" title="Removal & questions">
          <p>
            Want a subscription, a booking record, or an old email thread corrected or
            deleted? Write to{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-navy-700 underline">{CONTACT_EMAIL}</a>{' '}
            and we'll take care of it. If this policy ever changes in a way that matters,
            the effective date above changes with it.
          </p>
        </PolicySection>
      </article>
      <Footer />
    </div>
  );
}
